(function () {
  'use strict';

  if (window.TarteLocaleRouting) return;

  const PENDING_REDIRECT_STORAGE_KEY = 'tarte_locale_post_localization_redirect';
  const PENDING_REDIRECT_MAX_AGE_MS = 2 * 60 * 1000;
  let postLocalizationRedirectPending = false;
  const marketLanguageMap = readJsonObject('market-language-map');
  const localeRoutes = readJsonObject('locale-route-map');
  const labelOverrides = readLabelOverrides();

  function isObject(value) {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
  }

  function readJsonValue(id) {
    const element = document.getElementById(id);
    if (!element) return null;

    try {
      return JSON.parse(element.textContent || 'null');
    } catch (error) {
      console.warn('tarte-locale-routing: unable to parse ' + id, error);
      return null;
    }
  }

  function readJsonObject(id) {
    const value = readJsonValue(id);
    return isObject(value) ? value : {};
  }

  function readLabelOverrides() {
    let value = readJsonValue('locale-language-label-overrides');

    if (typeof value === 'string') {
      try {
        value = JSON.parse(value || '{}');
      } catch (error) {
        console.warn('tarte-locale-routing: language label overrides must be valid JSON', error);
        return {};
      }
    }

    if (!isObject(value)) {
      if (value !== null) {
        console.warn('tarte-locale-routing: language label overrides must be a JSON object');
      }
      return {};
    }

    return Object.keys(value).reduce(function (result, key) {
      if (typeof value[key] === 'string' && value[key].trim()) {
        result[normalizeLanguageCode(key)] = value[key].trim();
      }
      return result;
    }, {});
  }

  function normalizeLanguageCode(value) {
    return String(value || '').trim().replace(/_/g, '-').toLowerCase();
  }

  function baseLanguageCode(value) {
    return normalizeLanguageCode(value).split('-')[0];
  }

  function normalizeCountryCode(value) {
    return String(value || '').trim().toUpperCase();
  }

  function normalizeDirectory(value) {
    const directory = String(value || '').trim();
    if (!directory || directory === '/') return '';
    return ('/' + directory.replace(/^\/+|\/+$/g, '')).replace(/\/{2,}/g, '/');
  }

  function formatGeneratedName(name, languageCode, preserveRegion) {
    const formatted = preserveRegion
      ? String(name || '').trim()
      : String(name || '').replace(/\s*\([^)]*\)\s*$/, '').trim();
    if (!formatted) return '';

    try {
      return formatted.charAt(0).toLocaleUpperCase(languageCode) + formatted.slice(1);
    } catch (error) {
      return formatted.charAt(0).toUpperCase() + formatted.slice(1);
    }
  }

  function getShopifyLanguages(countryCode) {
    const country = marketLanguageMap[normalizeCountryCode(countryCode)];
    return country && Array.isArray(country.languages) ? country.languages : [];
  }

  function findShopifyLanguage(shopifyLanguages, languageCode, sourceLanguage) {
    const normalizedLanguage = normalizeLanguageCode(languageCode);
    const normalizedSource = normalizeLanguageCode(sourceLanguage);
    const exactLanguage = shopifyLanguages.find(function (language) {
      return normalizeLanguageCode(language.iso_code) === normalizedLanguage;
    });
    if (exactLanguage) return exactLanguage;

    const exactSource = shopifyLanguages.find(function (language) {
      return normalizeLanguageCode(language.iso_code) === normalizedSource;
    });
    if (exactSource) return exactSource;

    return shopifyLanguages.find(function (language) {
      return baseLanguageCode(language.iso_code) === baseLanguageCode(normalizedLanguage);
    }) || shopifyLanguages.find(function (language) {
      return baseLanguageCode(language.iso_code) === baseLanguageCode(normalizedSource);
    }) || null;
  }

  function nameFromIntl(languageCode, sourceLanguage, preserveRegion) {
    if (typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') return '';

    const normalizedLanguage = normalizeLanguageCode(languageCode);
    const displayLocale = normalizeLanguageCode(sourceLanguage) || normalizedLanguage;
    if (!displayLocale) return '';

    try {
      const formatter = new Intl.DisplayNames([displayLocale], { type: 'language' });
      return formatGeneratedName(
        formatter.of(preserveRegion ? normalizedLanguage : displayLocale),
        displayLocale,
        preserveRegion
      );
    } catch (error) {
      return '';
    }
  }

  function getLanguageName(languageCode, sourceLanguage, shopifyLanguage) {
    const normalizedLanguage = normalizeLanguageCode(languageCode);
    const normalizedSource = normalizeLanguageCode(sourceLanguage);
    const override = labelOverrides[normalizedLanguage]
      || labelOverrides[normalizedSource]
      || labelOverrides[baseLanguageCode(normalizedLanguage)]
      || labelOverrides[baseLanguageCode(normalizedSource)];

    if (override) return override;
    if (shopifyLanguage && shopifyLanguage.name) {
      const shopifyCode = normalizeLanguageCode(shopifyLanguage.iso_code);
      const isExactShopifyLanguage = shopifyCode === normalizedLanguage
        || shopifyCode === normalizedSource;
      if (!isExactShopifyLanguage && normalizedLanguage.indexOf('-') !== -1) {
        return nameFromIntl(normalizedLanguage, normalizedSource, true)
          || normalizedLanguage.toUpperCase();
      }
      return formatGeneratedName(shopifyLanguage.name, shopifyLanguage.iso_code);
    }

    return nameFromIntl(normalizedLanguage, normalizedSource)
      || normalizedLanguage.toUpperCase();
  }

  function getRouteLanguages(countryCode) {
    const routesByCountry = isObject(localeRoutes.routesByCountry) ? localeRoutes.routesByCountry : {};
    const routes = routesByCountry[normalizeCountryCode(countryCode)];
    return isObject(routes) ? routes : null;
  }

  function getLanguages(countryCode) {
    const shopifyLanguages = getShopifyLanguages(countryCode);
    const routeLanguages = getRouteLanguages(countryCode);

    if (routeLanguages && Object.keys(routeLanguages).length) {
      const matchedShopifyLanguageCodes = new Set();
      const languages = Object.keys(routeLanguages).reduce(function (result, languageCode) {
        const route = routeLanguages[languageCode];
        if (!isObject(route)) return result;

        const normalizedLanguage = normalizeLanguageCode(languageCode);
        const sourceLanguage = normalizeLanguageCode(route.sourceLanguage) || normalizedLanguage;
        const shopifyLanguage = findShopifyLanguage(shopifyLanguages, normalizedLanguage, sourceLanguage);
        const fallbackShopifyLanguage = shopifyLanguages[0] || null;
        if (shopifyLanguage) {
          matchedShopifyLanguageCodes.add(normalizeLanguageCode(shopifyLanguage.iso_code));
        }

        result.push({
          iso_code: normalizedLanguage,
          source_language: sourceLanguage,
          shopify_locale_code: shopifyLanguage
            ? shopifyLanguage.iso_code
            : (fallbackShopifyLanguage ? fallbackShopifyLanguage.iso_code : normalizedLanguage),
          display_name: getLanguageName(normalizedLanguage, sourceLanguage, shopifyLanguage),
          directory: normalizeDirectory(route.directory),
          has_route: true
        });
        return result;
      }, []);

      shopifyLanguages.forEach(function (shopifyLanguage) {
        const normalizedLanguage = normalizeLanguageCode(shopifyLanguage.iso_code);
        const alreadyIncluded = languages.some(function (language) {
          return normalizeLanguageCode(language.iso_code) === normalizedLanguage
            || normalizeLanguageCode(language.source_language) === normalizedLanguage;
        }) || matchedShopifyLanguageCodes.has(normalizedLanguage);
        if (alreadyIncluded) return;

        languages.push({
          iso_code: normalizedLanguage,
          source_language: normalizedLanguage,
          shopify_locale_code: shopifyLanguage.iso_code,
          display_name: getLanguageName(normalizedLanguage, normalizedLanguage, shopifyLanguage),
          directory: '',
          has_route: false
        });
      });

      return languages;
    }

    return shopifyLanguages.map(function (language) {
      const normalizedLanguage = normalizeLanguageCode(language.iso_code);
      return {
        iso_code: normalizedLanguage,
        source_language: normalizedLanguage,
        shopify_locale_code: language.iso_code,
        display_name: getLanguageName(normalizedLanguage, normalizedLanguage, language),
        directory: '',
        has_route: false
      };
    });
  }

  function findLanguage(languages, preferredLanguage) {
    const preferred = normalizeLanguageCode(preferredLanguage);
    if (!preferred) return null;

    return languages.find(function (language) {
      return normalizeLanguageCode(language.iso_code) === preferred;
    }) || languages.find(function (language) {
      return normalizeLanguageCode(language.source_language) === preferred;
    }) || languages.find(function (language) {
      return baseLanguageCode(language.iso_code) === baseLanguageCode(preferred);
    }) || languages.find(function (language) {
      return baseLanguageCode(language.source_language) === baseLanguageCode(preferred);
    }) || null;
  }

  function getPreferredLanguage(countryCode, preferredLanguage) {
    const languages = getLanguages(countryCode);
    const preferred = findLanguage(languages, preferredLanguage);
    if (preferred) return preferred.iso_code;

    const defaultShopifyLanguage = getShopifyLanguages(countryCode)[0];
    const defaultLanguage = defaultShopifyLanguage
      ? findLanguage(languages, defaultShopifyLanguage.iso_code)
      : null;
    return defaultLanguage
      ? defaultLanguage.iso_code
      : (languages[0] ? languages[0].iso_code : null);
  }

  function pathMatchesDirectory(pathname, directory) {
    return directory && (pathname === directory || pathname.indexOf(directory + '/') === 0);
  }

  function getActiveLanguage(countryCode, fallbackLanguage, pathname) {
    const languages = getLanguages(countryCode);
    const currentPath = pathname || window.location.pathname;
    const routeMatch = languages
      .filter(function (language) {
        return language.has_route && pathMatchesDirectory(currentPath, language.directory);
      })
      .sort(function (firstLanguage, secondLanguage) {
        return secondLanguage.directory.length - firstLanguage.directory.length;
      })[0];

    if (routeMatch) return routeMatch.iso_code;
    return getPreferredLanguage(countryCode, fallbackLanguage);
  }

  function allRouteDirectories() {
    if (!isObject(localeRoutes.routesByCountry)) return [];

    const directories = [];
    Object.keys(localeRoutes.routesByCountry).forEach(function (countryCode) {
      const routes = localeRoutes.routesByCountry[countryCode];
      if (!isObject(routes)) return;
      Object.keys(routes).forEach(function (languageCode) {
        const route = routes[languageCode];
        const directory = isObject(route) ? normalizeDirectory(route.directory) : '';
        if (directory && directories.indexOf(directory) === -1) directories.push(directory);
      });
    });
    return directories.sort(function (firstDirectory, secondDirectory) {
      return secondDirectory.length - firstDirectory.length;
    });
  }

  function normalizeSameOriginPath(pathname) {
    return '/' + String(pathname || '').replace(/\\/g, '/').replace(/^\/+/, '');
  }

  function isSafeSameOriginPath(value) {
    return typeof value === 'string'
      && value.indexOf('/') === 0
      && value.indexOf('//') !== 0
      && value.indexOf('/\\') !== 0;
  }

  function pathWithoutLocaleDirectory(pathname) {
    const shopifyRoot = window.Shopify && window.Shopify.routes
      ? normalizeDirectory(window.Shopify.routes.root)
      : '';
    const directories = allRouteDirectories();
    if (shopifyRoot && directories.indexOf(shopifyRoot) === -1) directories.push(shopifyRoot);
    directories.sort(function (firstDirectory, secondDirectory) {
      return secondDirectory.length - firstDirectory.length;
    });

    const directory = directories.find(function (candidate) {
      return pathMatchesDirectory(pathname, candidate);
    });
    if (!directory) return normalizeSameOriginPath(pathname);
    return normalizeSameOriginPath(pathname.slice(directory.length));
  }

  function pathWithLocaleDirectory(pathname, directory) {
    const remainder = pathWithoutLocaleDirectory(pathname);
    if (!directory) {
      const shopifyRoot = window.Shopify && window.Shopify.routes
        ? normalizeDirectory(window.Shopify.routes.root)
        : '';
      return shopifyRoot + (remainder === '/' ? '' : remainder) || '/';
    }
    return directory + (remainder === '/' ? '' : remainder);
  }

  function getLocalizationTarget(countryCode, languageCode, location, currentCountryCode) {
    const languages = getLanguages(countryCode);
    const language = findLanguage(languages, languageCode);
    const currentLocation = location || window.location;
    const selectedCountry = normalizeCountryCode(countryCode);
    const activeCountry = normalizeCountryCode(
      currentCountryCode || (window.Shopify && window.Shopify.country)
    );
    const unprefixedReturnTo = pathWithoutLocaleDirectory(currentLocation.pathname)
      + currentLocation.search
      + currentLocation.hash;

    if (!language) {
      return { navigationMode: 'form', localeCode: languageCode, returnTo: unprefixedReturnTo };
    }

    if (localeRoutes.sourceType !== 'third_party' || !language.has_route) {
      return {
        navigationMode: 'form',
        localeCode: language.shopify_locale_code,
        returnTo: unprefixedReturnTo
      };
    }

    if (!language.directory) {
      const currentShopifyLocale = normalizeLanguageCode(
        window.Shopify && window.Shopify.locale
      );
      const targetShopifyLocale = normalizeLanguageCode(language.shopify_locale_code);
      const canReuseCurrentShopifyRoot = activeCountry
        && selectedCountry === activeCountry
        && currentShopifyLocale
        && targetShopifyLocale === currentShopifyLocale;

      if (!canReuseCurrentShopifyRoot) {
        return {
          navigationMode: 'form',
          localeCode: language.shopify_locale_code,
          returnTo: unprefixedReturnTo
        };
      }
    }

    const thirdPartyReturnTo = pathWithLocaleDirectory(currentLocation.pathname, language.directory)
      + currentLocation.search
      + currentLocation.hash;
    if (!isSafeSameOriginPath(thirdPartyReturnTo)) {
      return {
        navigationMode: 'form',
        localeCode: language.shopify_locale_code,
        returnTo: unprefixedReturnTo
      };
    }
    if (!activeCountry || selectedCountry !== activeCountry) {
      return {
        navigationMode: 'form',
        localeCode: language.shopify_locale_code,
        returnTo: thirdPartyReturnTo,
        postLocalizationRedirect: thirdPartyReturnTo
      };
    }

    return {
      navigationMode: 'direct',
      localeCode: language.shopify_locale_code,
      returnTo: thirdPartyReturnTo
    };
  }

  function queuePostLocalizationRedirect(countryCode, returnTo) {
    if (!isSafeSameOriginPath(returnTo)) return;

    try {
      window.sessionStorage.setItem(PENDING_REDIRECT_STORAGE_KEY, JSON.stringify({
        countryCode: normalizeCountryCode(countryCode),
        returnTo: returnTo,
        createdAt: Date.now()
      }));
    } catch (error) {
      // The market switch still works when session storage is unavailable.
    }
  }

  function resumePostLocalizationRedirect() {
    let pending;
    try {
      pending = JSON.parse(window.sessionStorage.getItem(PENDING_REDIRECT_STORAGE_KEY) || 'null');
    } catch (error) {
      pending = null;
    }
    if (!isObject(pending)) return;

    const currentCountry = normalizeCountryCode(window.Shopify && window.Shopify.country);
    if (!currentCountry) return;

    try {
      window.sessionStorage.removeItem(PENDING_REDIRECT_STORAGE_KEY);
    } catch (error) {
      // The stored redirect cannot be reused after this page load.
    }

    const isFresh = Number.isFinite(pending.createdAt)
      && Date.now() - pending.createdAt <= PENDING_REDIRECT_MAX_AGE_MS;
    const isSafePath = isSafeSameOriginPath(pending.returnTo);
    if (!isFresh || !isSafePath || normalizeCountryCode(pending.countryCode) !== currentCountry) return;

    const currentPath = window.location.pathname + window.location.search + window.location.hash;
    if (currentPath !== pending.returnTo) {
      postLocalizationRedirectPending = true;
      window.location.replace(pending.returnTo);
    }
  }

  function isPostLocalizationRedirectPending() {
    return postLocalizationRedirectPending;
  }

  function isSameDocumentTarget(returnTo, location) {
    if (!isSafeSameOriginPath(returnTo)) return false;
    const currentLocation = location || window.location;
    const hashIndex = returnTo.indexOf('#');
    const targetPathAndSearch = hashIndex === -1 ? returnTo : returnTo.slice(0, hashIndex);
    return targetPathAndSearch === currentLocation.pathname + currentLocation.search;
  }

  window.TarteLocaleRouting = {
    marketLanguageMap: marketLanguageMap,
    localeRoutes: localeRoutes,
    getLanguages: getLanguages,
    getPreferredLanguage: getPreferredLanguage,
    getActiveLanguage: getActiveLanguage,
    getLocalizationTarget: getLocalizationTarget,
    queuePostLocalizationRedirect: queuePostLocalizationRedirect,
    isPostLocalizationRedirectPending: isPostLocalizationRedirectPending,
    isSameDocumentTarget: isSameDocumentTarget
  };

  resumePostLocalizationRedirect();
})();
